// Single responsibility: compute ranked dashboard search results from data inputs.
import { useMemo } from "react";

import {
  RESULT_TYPES,
  SEARCH_MAX_ACTION_RESULTS,
  SEARCH_MAX_INVENTORY_RESULTS,
  SEARCH_MAX_RESULTS,
  SEARCH_MIN_QUERY_LENGTH,
} from "./searchConstants";
import { normalizeString, scoreActionItem, scoreMedicationItem, sortByScore } from "./searchUtils";
import { breadcrumbs } from "@/lib/logger/sentryBreadcrumb";
import { EVENTS } from "@/lib/analytics/trackEvent";
import { safeTrack } from "@/lib/safeTrack";

export function useDashboardSearchEngine({ query, medications, actions }) {
  const results = useMemo(() => {
    const normalizedQuery = normalizeString(query);
    if (!normalizedQuery || normalizedQuery.length < SEARCH_MIN_QUERY_LENGTH) return [];

    const maxInventoryResults = Math.min(SEARCH_MAX_INVENTORY_RESULTS, Math.floor(SEARCH_MAX_RESULTS / 2));
    const maxActionResults = Math.min(SEARCH_MAX_ACTION_RESULTS, Math.floor(SEARCH_MAX_RESULTS / 2));

    const medicationResults = sortByScore(
      (medications || [])
        .map((medicationLine) => ({
          item: medicationLine,
          score: scoreMedicationItem(normalizedQuery, medicationLine),
        }))
        .filter((scoredMedication) => scoredMedication.score > 0),
    )
      .slice(0, maxInventoryResults)
      .map(({ item, score }) => {
        const statusLabel =
          item?.status === "item_expired" ? "expired" : item?.status === "item_moved" ? "moved" : "active";
        return {
          id: `med-${item.id}`,
          type: RESULT_TYPES.INVENTORY,
          label: `${item.name || "Unnamed"}${item.dosage ? ` · ${item.dosage}` : ""}`,
          sublabel: `${statusLabel} · qty ${Number(item.quantity || 0)} ${item.unit || "unit"}`,
          score,
          originalItem: item,
        };
      });

    const actionResults = sortByScore(
      (actions || [])
        .map((actionItem) => ({
          item: actionItem,
          score: scoreActionItem(normalizedQuery, actionItem),
        }))
        .filter((scoredAction) => scoredAction.score > 0),
    )
      .slice(0, maxActionResults)
      .map(({ item, score }) => ({
        id: item.id,
        type: RESULT_TYPES.ACTION,
        label: item.title,
        sublabel: item.subtitle,
        score,
        originalItem: item,
      }));

    const merged = [...medicationResults, ...actionResults].slice(0, SEARCH_MAX_RESULTS);
    if (normalizedQuery.length >= SEARCH_MIN_QUERY_LENGTH) {
      breadcrumbs.searchQueried(query, merged.length);
      safeTrack(
        async () => {
          await fetch("/api/analytics/track", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              eventName: EVENTS.SEARCH_QUERY,
              properties: { query, resultCount: merged.length },
            }),
            keepalive: true,
          });
        },
        "dashboard_search_query",
      );
      if (merged.length === 0) {
        safeTrack(
          async () => {
            await fetch("/api/analytics/track", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                eventName: EVENTS.SEARCH_ZERO_RESULTS,
                properties: { query, resultCount: 0 },
              }),
              keepalive: true,
            });
          },
          "dashboard_search_zero_results",
        );
      }
    }
    return merged;
  }, [actions, medications, query]);

  return { results, isSearching: false };
}
